import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { CmsSchema } from '@/cms/content';
import { useCmsFaq } from '@/components/cms/useCmsFaq';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MapPin, Clock, ChevronDown, ArrowRight, Mail } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { phoneValidationMessage } from '@/lib/phone';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { useConsent } from '@/context/ConsentContext';
import { KONTAKT_CONTENT } from '@/data/kontaktContent';

/* ─── Tokens ─────────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#1a1a1a';       // nagłówki
const BROWN  = '#5a3e28';       // ciepły brąz — akcent i podtytuły
const MUTED  = '#7a7068';       // przytłumiony tekst (home page)
const LINE   = 'rgba(210,60,120,0.22)';
// Jednolite białe tło sekcji.
const S1     = '#fff';
const S2     = '#fff';
const S3     = '#fff';
const S4     = '#fff';
const S5     = '#fff';

const MAP_EMBEDS = {
  bukietowa: 'https://www.google.com/maps?q=al.+Warszawska+23,+Lublin,+Poland&language=pl&z=16&output=embed',
  bursztynowa: 'https://www.google.com/maps?q=ul.+Bursztynowa+17,+Lublin,+Poland&language=pl&z=16&output=embed',
};

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport:    { once: true, margin: '-40px' },
  transition:  { duration: 0.55, delay, ease },
});

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

/* ─── 1. HERO ────────────────────────────────────────────────── */
function Hero() {
  return (
    <section style={{ background: '#fff', minHeight: 'calc(52dvh - 45px)', padding: 'calc(var(--page-breadcrumb-height, 44px) + 12px) 0 24px', display: 'flex', alignItems: 'center', position: 'relative' }}>
      <PageBreadcrumb crumbs={[{ label: 'Kontakt' }]} />

      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease }} style={{ textAlign: 'center' }}>
          <h1 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(2.8rem, 5.5vw, 5rem)',
            fontWeight: 400, lineHeight: 1.05, color: DARK,
            margin: '0 0 24px',
          }}>
            <CmsEditorialText id="information.hero.headingLine1" value={KONTAKT_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br />
            <em style={{ color: BROWN, fontStyle: 'italic' }}><CmsEditorialText id="information.hero.headingAccent" value={KONTAKT_CONTENT.hero.headingAccent} section="Wstęp" label="Wyróżniona część nagłówka" /></em>
          </h1>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.5vw, 16px)', color: MUTED, lineHeight: 1.85, margin: '0 auto 36px', maxWidth: 520 }}>
            <CmsEditorialText id="information.hero.body" value={KONTAKT_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href="tel:+48605030728" style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: '#fff', textDecoration: 'none', background: BROWN,
              padding: '14px 28px', transition: 'opacity 0.2s',
            }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.82')}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
            >
              <Phone size={12} />
              <CmsEditorialText id="information.hero.ctaCallLabel" value={KONTAKT_CONTENT.hero.ctaCallLabel} section="Wstęp" label="Tekst przycisku" />
            </a>
            <a href="#formularz" style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: BROWN, textDecoration: 'none',
              border: `1px solid ${GOLD}66`, padding: '14px 28px',
              transition: 'border-color 0.2s',
            }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = `${GOLD}66`)}
            >
              <CmsEditorialText id="information.hero.ctaWriteLabel" value={KONTAKT_CONTENT.hero.ctaWriteLabel} section="Wstęp" label="Tekst przycisku" />
              <ArrowRight size={12} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── 2. STORES ──────────────────────────────────────────────── */
function StoresSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(64px, 8vw, 96px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ marginBottom: 'clamp(40px, 5vw, 80px)', textAlign: 'center' }}>
          <SectionLabel text={KONTAKT_CONTENT.stores.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 400, color: BROWN, margin: 0, lineHeight: 1.1 }}>
            {KONTAKT_CONTENT.stores.heading}
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 16 }}>
          {KONTAKT_CONTENT.stores.items.map((s, i) => (
            <motion.div key={s.name} {...fadeUp(i * 0.1)}
              style={{ background: '#fff', padding: 'clamp(32px, 4vw, 52px)', display: 'flex', flexDirection: 'column', gap: 0, borderTop: `3px solid ${GOLD}` }}
            >
              {/* Store number + name */}
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 11, color: `${GOLD}99`, letterSpacing: '0.15em', marginBottom: 10 }}>
                {s.index}
              </span>
              <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: BROWN, margin: '0 0 6px', lineHeight: 1 }}>
                Kwiaciarnia<br /><em style={{ color: DARK, fontStyle: 'normal' }}>{s.name}</em>
              </h3>

              {/* Address */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '20px 0 6px' }}>
                <MapPin size={13} color={GOLD} style={{ marginTop: 3, flexShrink: 0 }} />
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.6 }}>
                  {s.address}<br />{s.city}
                </p>
              </div>

              {/* Phone */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 28px' }}>
                <Phone size={13} color={GOLD} style={{ flexShrink: 0 }} />
                <a href={s.tel} style={{
                  fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(20px, 2.5vw, 26px)',
                  fontWeight: 400, color: BROWN, textDecoration: 'none', letterSpacing: '0.02em',
                  transition: 'color 0.2s',
                }}
                  onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.color = GOLD)}
                  onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.color = BROWN)}
                >
                  {s.phone}
                </a>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a href={s.tel} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: '#fff', textDecoration: 'none', background: BROWN,
                  padding: '12px 22px', transition: 'opacity 0.2s', flexShrink: 0,
                }}
                  onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.8')}
                  onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
                >
                  <Phone size={11} />
                  {KONTAKT_CONTENT.stores.callLabel}
                </a>
                <a href={s.map} target="_blank" rel="noopener noreferrer" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                  color: BROWN, textDecoration: 'none',
                  border: `1px solid ${GOLD}55`, padding: '12px 22px',
                  transition: 'border-color 0.2s, color 0.2s', flexShrink: 0,
                }}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = GOLD; el.style.color = GOLD; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = `${GOLD}55`; el.style.color = BROWN; }}
                >
                  <MapPin size={11} />
                  {KONTAKT_CONTENT.stores.routeLabel}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 3. HOURS ───────────────────────────────────────────────── */
function HoursSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 7vw, 80px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(32px, 6vw, 96px)', flexWrap: 'wrap', textAlign: 'center' }}>
          {/* Left */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid ${GOLD}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Clock size={14} color={GOLD} />
              </div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>
                {KONTAKT_CONTENT.hours.eyebrow}
              </p>
            </div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.12em', color: MUTED, margin: 0, textTransform: 'uppercase' }}>
              {KONTAKT_CONTENT.hours.subLabel}
            </p>
          </div>

          {/* Right: times */}
          <div style={{ display: 'flex', gap: 'clamp(32px, 5vw, 80px)', flexWrap: 'wrap' }}>
            {KONTAKT_CONTENT.hours.rows.map(row => (
              <div key={row.label}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: MUTED, margin: '0 0 8px' }}>
                  {row.label}
                </p>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1 }}>
                  {row.time}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── 4. MAP ─────────────────────────────────────────────────── */
function MapSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [embeds, setEmbeds] = useState<{ bukietowa: string; bursztynowa: string } | null>(null);
  const loaded = useRef(false);
  const { canUseIntegration, grantConsent } = useConsent();
  const canLoadMaps = canUseIntegration('google-maps-embed');

  useEffect(() => {
    if (!canLoadMaps) {
      loaded.current = false;
      setEmbeds(null);
      return;
    }
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loaded.current) {
          loaded.current = true;
          setEmbeds(MAP_EMBEDS);
          observer.disconnect();
        }
      },
      { rootMargin: '0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadMaps]);

  return (
    <section ref={sectionRef} style={{ background: '#fff', padding: 'clamp(64px, 8vw, 96px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ marginBottom: 'clamp(32px, 4vw, 48px)', textAlign: 'center' }}>
          <SectionLabel text={KONTAKT_CONTENT.map.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: BROWN, margin: 0, lineHeight: 1.1 }}>
            {KONTAKT_CONTENT.map.heading}
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 16 }}>
          {KONTAKT_CONTENT.stores.items.map((s, i) => (
            <motion.div key={s.name} {...fadeUp(i * 0.1)}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, margin: '0 0 12px' }}>
                {s.index} - Kwiaciarnia {s.name}
              </p>
              {canLoadMaps && embeds ? (
                <iframe
                  src={i === 0 ? embeds.bukietowa : embeds.bursztynowa}
                  width="100%"
                  height="clamp(260px, 40vw, 320px)"
                  style={{ border: 0, display: 'block', filter: 'grayscale(0.15)', width: '100%', height: 'clamp(260px, 40vw, 320px)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Mapa - Kwiaciarnia ${s.name}`}
                />
              ) : !canLoadMaps ? (
                <button
                  type="button"
                  onClick={() => grantConsent('functional')}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, height: 'clamp(260px, 40vw, 320px)', background: S2, border: `1px solid ${LINE}`, textDecoration: 'none', transition: 'background 0.2s' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = `${GOLD}0d`)}
                  onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = S2)}
                >
                  <MapPin size={24} color={GOLD} />
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED, margin: 0 }}>
                    {s.address}, {s.city}
                  </p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, margin: 0 }}>
                    {KONTAKT_CONTENT.map.showMapLabel}
                  </p>
                </button>
              ) : (
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, height: 'clamp(260px, 40vw, 320px)', background: '#fff', border: `1px solid ${LINE}` }}
                >
                  <MapPin size={24} color={GOLD} />
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED, margin: 0 }}>
                    {KONTAKT_CONTENT.map.loadingLabel}
                  </p>
                </div>
              )}
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapPin size={12} color={MUTED as unknown as string} />
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, margin: 0 }}>
                  {s.address}, {s.city}
                </p>
                <a href={s.map} target="_blank" rel="noopener noreferrer"
                  style={{ marginLeft: 'auto', fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD, textDecoration: 'none' }}>
                  {KONTAKT_CONTENT.map.routeLabel}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 5. PHONE CTA ───────────────────────────────────────────── */


/* ─── 6. CONTACT FORM ────────────────────────────────────────── */
type FormState = 'idle' | 'sending' | 'ok' | 'error';

function FormSection() {
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [phone,   setPhone]   = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status,  setStatus]  = useState<FormState>('idle');
  const [errMsg,  setErrMsg]  = useState('');

  const inputStyle: React.CSSProperties = {
    fontFamily: 'DM Sans, sans-serif', fontSize: 16,
    border: `1px solid ${LINE}`, background: '#fff',
    padding: '13px 14px', outline: 'none', color: BROWN,
    width: '100%', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    minHeight: 48,
  };
  const phoneError = phoneValidationMessage(phone, true);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (phoneError) {
      setStatus('error');
      setErrMsg(phoneError);
      return;
    }
    setStatus('sending'); setErrMsg('');
    try {
      const res = await fetch(`${BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, subject, message }),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (data.ok) {
        setStatus('ok');
        setName(''); setEmail(''); setPhone(''); setSubject(''); setMessage('');
      } else {
        setStatus('error');
        setErrMsg(data.error ?? KONTAKT_CONTENT.form.genericErrorMessage);
      }
    } catch {
      setStatus('error');
      setErrMsg(KONTAKT_CONTENT.form.networkErrorMessage);
    }
  }

  return (
    <section id="formularz" style={{ background: '#fff', padding: 'clamp(64px, 8vw, 96px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 'clamp(32px, 7vw, 100px)', alignItems: 'start' }}>

          {/* Left: description */}
          <motion.div {...fadeUp()} style={{ textAlign: 'center' }}>
            <SectionLabel text={KONTAKT_CONTENT.form.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3.2rem)', fontWeight: 400, color: BROWN, margin: '0 0 20px', lineHeight: 1.1 }}>
              {KONTAKT_CONTENT.form.headingLine1}<br />{KONTAKT_CONTENT.form.headingLine2}
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, margin: '0 0 32px' }}>
              {KONTAKT_CONTENT.form.body}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', background: '#fff', border: `1px solid ${LINE}` }}>
              <Mail size={13} color={GOLD} style={{ flexShrink: 0 }} />
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED, margin: 0 }}>
                {KONTAKT_CONTENT.form.email}
              </p>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div {...fadeUp(0.1)}>
            <AnimatePresence mode="wait">
              {status === 'ok' ? (
                <motion.div key="ok" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ padding: 'clamp(32px, 4vw, 48px)', background: '#fff', border: `1px solid ${GOLD}44`, textAlign: 'center' }}
                >
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', fontWeight: 400, color: BROWN, margin: '0 0 12px' }}>
                    {KONTAKT_CONTENT.form.successHeading}
                  </p>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, margin: '0 0 24px', lineHeight: 1.75 }}>
                    {KONTAKT_CONTENT.form.successBody}
                  </p>
                  <button onClick={() => setStatus('idle')}
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: GOLD, background: 'none', border: `1px solid ${GOLD}55`, padding: '12px 20px', minHeight: 44, cursor: 'pointer' }}
                  >
                    {KONTAKT_CONTENT.form.successResetLabel}
                  </button>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 10 }}>
                    <div>
                      <label htmlFor="kontakt-imie" className="sr-only">Imię</label>
                      <input
                        id="kontakt-imie"
                        type="text" placeholder={KONTAKT_CONTENT.form.namePlaceholder} required value={name} onChange={e => setName(e.target.value)}
                        style={inputStyle}
                        onFocus={e => ((e.currentTarget as HTMLInputElement).style.borderColor = GOLD)}
                        onBlur={e => ((e.currentTarget as HTMLInputElement).style.borderColor = LINE)}
                      />
                    </div>
                    <div>
                      <label htmlFor="kontakt-email" className="sr-only">E-mail</label>
                      <input
                        id="kontakt-email"
                        type="email" placeholder={KONTAKT_CONTENT.form.emailPlaceholder} required value={email} onChange={e => setEmail(e.target.value)}
                        style={inputStyle}
                        onFocus={e => ((e.currentTarget as HTMLInputElement).style.borderColor = GOLD)}
                        onBlur={e => ((e.currentTarget as HTMLInputElement).style.borderColor = LINE)}
                      />
                    </div>
                  </div>
                  <label htmlFor="kontakt-telefon" className="sr-only">Telefon</label>
                  <PhoneInput
                    id="kontakt-telefon"
                    placeholder="Numer telefonu"
                    required
                    value={phone}
                    onChange={value => {
                      setPhone(value);
                      if (status === 'error') {
                        setStatus('idle');
                        setErrMsg('');
                      }
                    }}
                    error={status === 'error' ? phoneError ?? undefined : undefined}
                    style={inputStyle}
                  />
                  <div style={{ position: 'relative' }}>
                    <label htmlFor="kontakt-temat" className="sr-only">Temat wiadomości</label>
                    <select id="kontakt-temat" value={subject} onChange={e => setSubject(e.target.value)}
                      style={{ ...inputStyle, appearance: 'none', cursor: 'pointer', paddingRight: 36, color: subject ? BROWN : MUTED }}
                    >
                      <option value="">{KONTAKT_CONTENT.form.subjectPlaceholder}</option>
                      {KONTAKT_CONTENT.form.topics.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <ChevronDown size={13} color={MUTED as unknown as string} style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                  <label htmlFor="kontakt-wiadomosc" className="sr-only">Wiadomość</label>
                  <textarea
                    id="kontakt-wiadomosc"
                    placeholder={KONTAKT_CONTENT.form.messagePlaceholder} required rows={5} value={message} onChange={e => setMessage(e.target.value)}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                    onFocus={e => ((e.currentTarget as HTMLTextAreaElement).style.borderColor = GOLD)}
                    onBlur={e => ((e.currentTarget as HTMLTextAreaElement).style.borderColor = LINE)}
                  />

                  {status === 'error' && (
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: '#c0392b', margin: 0, padding: '10px 12px', background: 'rgba(192,57,43,0.05)', border: '1px solid rgba(192,57,43,0.18)', overflowWrap: 'break-word' }}>
                      {errMsg}
                    </p>
                  )}

                  <button type="submit" disabled={status === 'sending'}
                    style={{
                      width: '100%', padding: '14px 0', minHeight: 44,
                      background: status === 'sending' ? `${BROWN}66` : BROWN,
                      color: '#fff', border: 'none',
                      cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                      fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => { if (status !== 'sending') (e.currentTarget as HTMLButtonElement).style.opacity = '0.82'; }}
                    onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
                  >
                    {status === 'sending' ? KONTAKT_CONTENT.form.submitLabelSending : KONTAKT_CONTENT.form.submitLabel}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── 7. SPECIAL OFFERS ──────────────────────────────────────── */
function SpecialsSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(64px, 8vw, 96px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 1, background: LINE }}>
          {KONTAKT_CONTENT.specials.map((s, i) => (
            <motion.div key={s.tag} {...fadeUp(i * 0.1)}
              style={{ background: '#fff', padding: 'clamp(32px, 4vw, 48px)', borderTop: `2px solid ${i === 0 ? GOLD : `${GOLD}55`}`, display: 'flex', flexDirection: 'column', gap: 0 }}
            >
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, margin: '0 0 20px' }}>
                <CmsEditorialText id={`information.specials.${i}.tag`} value={s.tag} section="Oferta" label={`Element ${i + 1}: nadtytuł`} />
              </p>
              <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.4rem, 2.2vw, 2rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.15 }}>
                <CmsEditorialText id={`information.specials.${i}.heading`} value={s.heading} section="Oferta" label={`Element ${i + 1}: nagłówek`} />
              </h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.8, margin: '0 0 20px', flexGrow: 1 }}>
                <CmsEditorialText id={`information.specials.${i}.text`} value={s.text} section="Oferta" label={`Element ${i + 1}: tekst`} multiline />
              </p>
              <a href={s.href}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, fontFamily: 'DM Sans, sans-serif', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: BROWN, textDecoration: 'none', transition: 'gap 0.2s, color 0.2s' }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.gap = '12px'; el.style.color = GOLD; }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.gap = '8px'; el.style.color = BROWN; }}
              >
                {s.cta}
                <ArrowRight size={11} />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 8. FAQ ─────────────────────────────────────────────────── */
function FaqSection() {
  const cmsFaq = useCmsFaq(KONTAKT_CONTENT.faq.items);
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section style={{ background: '#fff', padding: 'clamp(64px, 8vw, 96px) 0' }}>

      <CmsSchema fields={cmsFaq.fields} />
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ marginBottom: 'clamp(36px, 4vw, 52px)', textAlign: 'center' }}>
          <SectionLabel text={KONTAKT_CONTENT.faq.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: BROWN, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.faq.heading" value={KONTAKT_CONTENT.faq.heading} section="Pytania i odpowiedzi" label="Nagłówek" />
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 0, borderTop: `1px solid ${LINE}` }}>
          {cmsFaq.items.map((item, i) => (
            <motion.div key={i} {...fadeUp(i * 0.04)} style={{ borderBottom: `1px solid ${LINE}` }}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                  padding: '20px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px, 1.8vw, 20px)', fontWeight: 400, color: BROWN, margin: 0, lineHeight: 1.3 }}>
                  <span {...cmsFaq.text(`${item.cmsKey}.q`)}>{item.q}</span>
                </p>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.25, ease }}
                  style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', border: `1px solid ${GOLD}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <ChevronDown size={13} color={GOLD} />
                </motion.div>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease }}
                    style={{ overflow: 'hidden' }}
                  >
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.82, margin: '0 0 20px', maxWidth: 680 }}>
                      <span {...cmsFaq.text(`${item.cmsKey}.a`)}>{item.a}</span>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── PAGE ───────────────────────────────────────────────────── */
export default function KontaktPage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.kontakt);
  return (
    <div style={{ background: '#fff' }}>
      <main>
        <Hero />
        <StoresSection />
        <HoursSection />
        <MapSection />
        <FormSection />
        <SpecialsSection />
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
}
